import { z } from 'zod';
import { BackendError } from '@/api/adapter/client';
import {
  getOperation,
  registerWorker,
  updateWorker,
} from '@/api/adapter/onboarding';
import { useAuthStore } from '@/stores/authStore';
import type {
  OperationView,
  WorkerRegistration,
  Qualifications,
  CalendarDefinition,
} from '@/types/session';

const registrationSchema = z.object({
  displayName: z.string().min(1),
  qualifications: z.object({
    roleIds: z.array(z.number().int().positive()),
    skillIds: z.array(z.number().int().positive()),
  }),
  calendar: z.object({
    defaultWeeklyMinutes: z.number().int().min(0).max(10080),
    overrides: z.array(z.never()).length(0),
  }),
});
type CommandPayloads = {
  registration: WorkerRegistration;
  qualifications: { qualifications: Qualifications };
  calendar: CalendarDefinition;
};
export type WorkerCommand = keyof CommandPayloads;
const commandSchemas = {
  registration: registrationSchema,
  qualifications: z.object({
    qualifications: registrationSchema.shape.qualifications,
  }),
  calendar: z.object({
    defaultWeeklyMinutes: z.number().int().min(0).max(10080),
    overrides: z.array(
      z.object({
        week: z.object({
          isoYear: z.number().int(),
          week: z.number().int().min(1).max(53),
        }),
        capacity: z.number().int().min(0).max(4294967295),
      })
    ),
  }),
};
const storageKey = (principal: string, kind: WorkerCommand) =>
  `abako-worker-${kind}:${principal}`;
export const WORKER_COMMAND_CHANGED = 'abako-worker-command-changed';
function writePending(
  principal: string,
  kind: WorkerCommand,
  value: unknown | null
) {
  if (value === null) sessionStorage.removeItem(storageKey(principal, kind));
  else
    sessionStorage.setItem(storageKey(principal, kind), JSON.stringify(value));
  window.dispatchEvent(
    new CustomEvent(WORKER_COMMAND_CHANGED, { detail: { principal, kind } })
  );
}
export function pendingWorkerCommand<K extends WorkerCommand>(
  principal: string,
  kind: K
): {
  key: string;
  operationId?: string;
  payload: CommandPayloads[K];
} | null {
  const raw = sessionStorage.getItem(storageKey(principal, kind));
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error(
      'The saved worker request cannot be read. Check the existing worker state before resubmitting.'
    );
  }
  const result = z
    .object({
      key: z.string().regex(/^0x[0-9a-f]{32}$/),
      operationId: z
        .string()
        .regex(/^0x[0-9a-f]{32}$/)
        .optional(),
      payload: commandSchemas[kind],
    })
    .safeParse(value);
  if (!result.success)
    throw new Error(
      'The saved worker request is invalid. Check the existing worker state before resubmitting.'
    );
  return result.data as {
    key: string;
    operationId?: string;
    payload: CommandPayloads[K];
  };
}
export const pendingRegistration = (principal: string) =>
  pendingWorkerCommand(principal, 'registration');
export function operationResult(
  operation: OperationView,
  label = 'Worker update'
): 'success' | 'pending' {
  if (operation.status === 'Finalized') {
    if (operation.receipt?.outcome.type === 'Success') return 'success';
    if (!operation.receipt)
      throw new Error(
        'The operation has no receipt. Check its status again before submitting another request.'
      );
    throw new Error(
      `${label} failed: ${operation.errorCode ?? operation.receipt.outcome.code ?? operation.receipt.outcome.type}.`
    );
  }
  if (operation.status === 'Rejected' || operation.status === 'Expired') {
    throw new Error(
      `${label} ${operation.status.toLowerCase()}: ${operation.errorCode ?? 'please review and retry'}.`
    );
  }
  return 'pending';
}
export async function completeWorkerCommand<K extends WorkerCommand>(
  principal: string,
  kind: K,
  payload: CommandPayloads[K],
  onStatus: (message: string) => void,
  signal: AbortSignal
): Promise<boolean> {
  const assertOwner = () => {
    if (
      signal.aborted ||
      useAuthStore.getState().session?.principalId !== principal
    )
      throw new Error('Registration paused. Log in to resume it.');
  };
  assertOwner();
  let pending = pendingWorkerCommand(principal, kind);
  if (!pending) {
    pending = {
      key: '0x' + crypto.randomUUID().replace(/-/g, ''),
      payload: commandSchemas[kind].parse(payload) as CommandPayloads[K],
    };
    // Persist before sending: a lost response must not cause a duplicate command.
    writePending(principal, kind, pending);
  }
  if (!pending.operationId) {
    const reference = await (
      kind === 'registration'
        ? registerWorker(pending.payload as WorkerRegistration, pending.key)
        : updateWorker(kind, pending.payload, pending.key)
    ).catch((error: unknown) => {
      if (
        error instanceof BackendError &&
        [400, 413, 422].includes(error.statusCode)
      )
        writePending(principal, kind, null);
      throw error;
    });
    assertOwner();
    pending.operationId = reference.operationId;
    writePending(principal, kind, pending);
  }
  const deadline = Date.now() + 60_000;
  while (Date.now() < deadline) {
    assertOwner();
    const operation = await getOperation(pending.operationId);
    assertOwner();
    try {
      if (operationResult(operation) === 'success') {
        writePending(principal, kind, null);
        return true;
      }
    } catch (error) {
      if (
        operation.status === 'Rejected' ||
        operation.status === 'Expired' ||
        (operation.status === 'Finalized' && operation.receipt)
      ) {
        writePending(principal, kind, null);
      }
      throw error;
    }
    onStatus(
      operation.status === 'OutcomeUnknown'
        ? 'The worker request outcome is not yet known. Checking the same operation…'
        : 'Worker request accepted. Waiting for confirmation…'
    );
    await new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', finish);
        resolve();
      };
      const timer = setTimeout(finish, 2000);
      signal.addEventListener('abort', finish, { once: true });
    });
  }
  onStatus(
    'Worker request is still pending. Choose the check button to continue checking the same request.'
  );
  return false;
}

export function completeWorkerRegistration(
  principal: string,
  payload: WorkerRegistration,
  onStatus: (message: string) => void,
  signal: AbortSignal
) {
  return completeWorkerCommand(
    principal,
    'registration',
    payload,
    onStatus,
    signal
  );
}
